import type { Lang } from "./language";

export type Pathway = {
  index: string;
  level: string;
  title: string;
  work: string;
  description: string;
  bullets: string[];
  price: string;
  seats: string;
  duration: string;
};

export type Content = {
  nav: { about: string; experience: string; pathways: string; included: string; enroll: string };
  cta: { apply: string; guide: string };
  hero: {
    eyebrow: string;
    titleTop: string;
    titleAccent: string;
    titleBottom: string;
    tagline: string;
    lead: string;
    stats: { value: string; label: string }[];
  };
  about: { eyebrow: string; title: string; body: string[] };
  values: { eyebrow: string; title: string; items: { title: string; body: string }[] };
  experience: { eyebrow: string; title: string; lead: string; steps: { n: string; title: string; body: string }[] };
  pathways: {
    eyebrow: string;
    title: string;
    lead: string;
    note: string;
    priceLabel: string;
    seatsLabel: string;
    durationLabel: string;
    items: Pathway[];
  };
  publications: {
    eyebrow: string;
    titleTop: string;
    titleAccent: string;
    view: string;
    all: string;
    allUrl: string;
    items: { meta: string; title: string; source: string; url?: string }[];
  };
  instagram: {
    eyebrow: string;
    titleTop: string;
    titleAccent: string;
    lead: string;
    handle: string;
    cta: string;
    profileUrl: string;
  };
  included: { eyebrow: string; title: string; lead: string; items: { title: string; body: string }[] };
  global: { eyebrow: string; title: string; lead: string; items: { title: string; body: string }[] };
  enroll: {
    eyebrow: string;
    title: string;
    lead: string;
    points: string[];
    disclaimer: string;
    accountTitle: string;
    accountBody: string;
  };
  contact: { eyebrow: string; title: string; lead: string; whatsapp: string; email: string; instagram: string };
  footer: { rights: string; disclaimer: string };
  meta: { title: string; description: string };
};

const es: Content = {
  nav: {
    about: "Quiénes somos",
    experience: "Experiencia STM",
    pathways: "Rutas",
    included: "Qué incluye",
    enroll: "Inscripción",
  },
  cta: { apply: "Aplicar al programa", guide: "Conocer las rutas" },
  hero: {
    eyebrow: "Programa de mentoría",
    titleTop: "Share",
    titleAccent: "the",
    titleBottom: "Med",
    tagline: "La investigación empieza donde la curiosidad va más lejos.",
    lead: "Mentoría científica para estudiantes, residentes y médicos: convertimos el interés por publicar en un proceso formativo real, guiado y con proyectos que llegan al proceso editorial.",
    stats: [
      { value: "3", label: "Rutas de investigación" },
      { value: "1–4", label: "Meses por proyecto" },
      { value: "6–8", label: "Cupos por cohorte" },
    ],
  },
  about: {
    eyebrow: "Quiénes somos",
    title: "Investigación que se aprende haciéndola.",
    body: [
      "Share The Med (STM) es una iniciativa colaborativa de mentoría en investigación que conecta a estudiantes, residentes, médicos y profesionales de la salud a través de proyectos científicos estructurados.",
      "Nuestro modelo combina mentoría, escritura científica e investigación colaborativa para que cada participante desarrolle habilidades aplicables y contribuya a trabajo científico con sentido.",
      "Creemos que la investigación se aprende mediante participación, acompañamiento y colaboración interdisciplinaria.",
    ],
  },
  values: {
    eyebrow: "Valores STM",
    title: "Rigor que sí se ejecuta.",
    items: [
      {
        title: "Aprender haciendo investigación",
        body: "La participación activa en proyectos reales es el medio principal de aprendizaje.",
      },
      {
        title: "Rigor metodológico aplicado",
        body: "Cada proyecto se desarrolla bajo estándares alineados con el entorno científico actual.",
      },
      {
        title: "Trabajo en equipo con estructura",
        body: "Equipos guiados, con entregables definidos y roles claros desde el inicio.",
      },
      {
        title: "Expectativas claras desde el inicio",
        body: "Acompañamos el proceso; la aceptación editorial depende de cada revista.",
      },
      {
        title: "Cada integrante es responsable del resultado",
        body: "El avance del proyecto depende del compromiso de cada miembro del equipo.",
      },
      {
        title: "Crecer paso a paso",
        body: "Niveles que aumentan de forma gradual la complejidad y la autonomía del participante.",
      },
    ],
  },
  experience: {
    eyebrow: "La experiencia STM",
    title: "Una ruta estructurada, de la idea al manuscrito.",
    lead: "Un recorrido colaborativo diseñado para guiar al participante a lo largo de todo el proceso de investigación.",
    steps: [
      { n: "01", title: "Contacto inicial", body: "Primer acercamiento y exploración del proyecto." },
      { n: "02", title: "Revisión de perfil", body: "Valoración de antecedentes, intereses y disponibilidad." },
      { n: "03", title: "Inscripción y grupo", body: "Asignación según disponibilidad y requisitos de ingreso." },
      { n: "04", title: "Sesión inicial", body: "Presentación de mentores, equipo y proyecto." },
      { n: "05", title: "Desarrollo del proyecto", body: "Investigación colaborativa con mentoría continua." },
      { n: "06", title: "Proceso editorial", body: "Preparación del manuscrito y guía en el envío a revista." },
      { n: "07", title: "Alumni y nuevas oportunidades", body: "Nuevos proyectos y colaboración a largo plazo." },
    ],
  },
  pathways: {
    eyebrow: "Rutas de investigación",
    title: "Tres rutas. Distintos puntos de partida.",
    lead: "STM organiza los proyectos por nivel de complejidad metodológica, carga de trabajo y riesgo editorial. El Nivel STM clasifica trabajos, no personas.",
    note: "La disponibilidad de proyectos varía según los cupos de la cohorte vigente. Los precios cubren el acompañamiento académico y la dirección científica; no garantizan la aceptación o publicación del manuscrito.",
    priceLabel: "Por participante",
    seatsLabel: "Cupo máximo",
    durationLabel: "Duración estimada",
    items: [
      {
        index: "01",
        level: "Nivel Básico",
        title: "Ruta Básica",
        work: "Reporte de caso",
        description:
          "Primera experiencia en publicación científica, con acompañamiento cercano y baja complejidad metodológica.",
        bullets: [
          "Ideal para quien inicia su camino en investigación",
          "Bases sólidas de redacción científica",
          "Reporte de caso, caso con revisión de literatura o editorial",
        ],
        price: "$3,500 MXN",
        seats: "8 participantes",
        duration: "1 a 2 meses",
      },
      {
        index: "02",
        level: "Nivel Intermedio",
        title: "Ruta Intermedia",
        work: "Narrative Review",
        description:
          "Síntesis de literatura enfocada, con mentoría académica estructurada y mayor autonomía del participante.",
        bullets: [
          "Profundiza en el análisis crítico de la literatura",
          "Fortalece la comunicación científica",
          "Narrative Review, Scoping Review o serie de casos",
        ],
        price: "$4,500 MXN",
        seats: "8 participantes",
        duration: "1 a 3 meses",
      },
      {
        index: "03",
        level: "Nivel Avanzado",
        title: "Ruta Avanzada",
        work: "Revisión Sistemática",
        description:
          "Metodología estructurada y proceso riguroso orientado a síntesis de evidencia y journals indexados Q1–Q4.",
        bullets: [
          "Metodologías avanzadas de investigación",
          "Meta-análisis por invitación o evaluación previa",
          "Requiere valoración académica de la Dirección STM",
        ],
        price: "desde $6,000 MXN",
        seats: "6 participantes",
        duration: "3 a 4 meses",
      },
    ],
  },
  publications: {
    eyebrow: "Investigación científica",
    titleTop: "Publicaciones",
    titleAccent: "seleccionadas",
    view: "Ver",
    all: "Ver todas en Google Scholar",
    allUrl: "https://scholar.google.com/citations?hl=es",
    items: [
      {
        meta: "Cureus · Mayo 2026",
        title:
          "Testosterone Replacement Therapy as a Foundation for Body Composition Remodeling: Synergistic Roles of Resistance Training and Protein Intake",
        source: "Narrative Review · Cureus Journal of Medical Science",
      },
      {
        meta: "Springer Nature · Mayo 2026",
        title:
          "Myeloperoxidase Antineutrophil Cytoplasmic Antibody (MPO-ANCA)-Associated Usual Interstitial Pneumonia: Pathogenic Mechanisms, Diagnostic Challenges, and Clinical Implications",
        source: "Springer Nature · Structured Narrative Review",
      },
      {
        meta: "Revistas indexadas · 2025–2026",
        title:
          "Revisiones sistemáticas, meta-análisis y casos clínicos en cirugía, medicina interna y salud pública",
        source: "20+ publicaciones peer-reviewed en total",
      },
    ],
  },
  instagram: {
    eyebrow: "Comunidad STM",
    titleTop: "Síguenos en",
    titleAccent: "Instagram",
    lead: "Publicaciones, convocatorias y logros de cada cohorte, contados post a post.",
    handle: "@sharethemed",
    cta: "Ver perfil en Instagram",
    profileUrl: "https://www.instagram.com/sharethemed/",
  },
  included: {
    eyebrow: "Qué incluye",
    title: "Un acompañamiento completo.",
    lead: "Una experiencia guiada y colaborativa diseñada para sostener tu desarrollo en investigación.",
    items: [
      { title: "Mentoría estructurada", body: "Guía a lo largo de todo el proceso de investigación." },
      { title: "Apoyo en escritura científica", body: "Desarrollo de habilidades de redacción y comunicación." },
      { title: "Metodología de la investigación", body: "Apoyo para comprender y aplicar métodos de investigación." },
      { title: "Participación colaborativa", body: "Trabajo en un equipo multidisciplinario." },
      { title: "Coordinación administrativa", body: "Agenda, comunicación y organización del proyecto." },
      { title: "Orientación editorial", body: "Guía en la preparación y el envío del manuscrito." },
    ],
  },
  global: {
    eyebrow: "Entorno internacional",
    title: "Colaboración más allá de las fronteras.",
    lead: "STM reúne participantes de distintos países y perfiles profesionales para crear un entorno de investigación enriquecido por la diversidad de perspectivas.",
    items: [
      { title: "Participantes internacionales", body: "Colabora con colegas de varios países y construye red académica." },
      { title: "Colaboración multidisciplinaria", body: "Proyectos enriquecidos por experiencia y perfiles diversos." },
      { title: "Entorno de mentoría", body: "Mentores con experiencia comprometidos con tu desarrollo." },
      { title: "Exposición global", body: "Contacto con prácticas y estándares científicos internacionales." },
    ],
  },
  enroll: {
    eyebrow: "Participación e inscripción",
    title: "Cómo aplicar.",
    lead: "El proceso de admisión busca mantener grupos funcionales y proteger la calidad académica de cada proyecto.",
    points: [
      "Crea tu cuenta y completa la solicitud con tu perfil académico e intereses de investigación.",
      "La Dirección Académica STM revisa el perfil y confirma la ruta y el grupo disponible.",
      "Formalizas tu lugar con el pago de participación desde la plataforma.",
      "Compromiso con entregables, plazos y trabajo colaborativo del grupo.",
    ],
    disclaimer:
      "STM ofrece opciones de pago flexibles, incluidas alternativas para participantes internacionales. La aceptación y publicación quedan sujetas al proceso editorial y de revisión por pares de cada revista.",
    accountTitle: "Cuenta de participante y pagos en línea",
    accountBody:
      "Crea tu cuenta para enviar tu solicitud, dar seguimiento a la revisión académica y, una vez aceptado, realizar el pago de participación (MXN o USD) desde la plataforma.",
  },
  contact: {
    eyebrow: "Contacto",
    title: "Comienza tu camino en investigación.",
    lead: "Nuestro objetivo es que desarrolles una comprensión sólida y práctica de la investigación médica, que realmente impulse tu crecimiento profesional.",
    whatsapp: "WhatsApp",
    email: "Correo",
    instagram: "Instagram",
  },
  footer: {
    rights: "Todos los derechos reservados.",
    disclaimer: "STM acompaña el proceso de investigación; no garantiza la publicación del manuscrito.",
  },
  meta: {
    title: "Share The Med (STM) | Mentoría en investigación médica",
    description:
      "Programa de mentoría científica para estudiantes, residentes y médicos: reportes de caso, revisiones narrativas y revisiones sistemáticas con acompañamiento editorial.",
  },
};

const en: Content = {
  nav: {
    about: "Who we are",
    experience: "STM experience",
    pathways: "Pathways",
    included: "What's included",
    enroll: "Enrollment",
  },
  cta: { apply: "Apply to the program", guide: "Explore the pathways" },
  hero: {
    eyebrow: "Mentorship program",
    titleTop: "Share",
    titleAccent: "the",
    titleBottom: "Med",
    tagline: "Research starts where curiosity goes further.",
    lead: "Scientific mentorship for students, residents and physicians: we turn the interest in publishing into a real, guided training process with projects that reach the editorial stage.",
    stats: [
      { value: "3", label: "Research pathways" },
      { value: "1–4", label: "Months per project" },
      { value: "6–8", label: "Seats per cohort" },
    ],
  },
  about: {
    eyebrow: "Who we are",
    title: "Research learned by doing it.",
    body: [
      "Share The Med (STM) is a collaborative research mentorship initiative that connects students, residents, physicians and healthcare professionals through structured research projects.",
      "Our model combines mentorship, scientific writing and collaborative research to help participants develop practical research skills and contribute to meaningful scientific work.",
      "We believe research is learned through participation, guidance and interdisciplinary collaboration.",
    ],
  },
  values: {
    eyebrow: "STM values",
    title: "Rigor that actually gets executed.",
    items: [
      {
        title: "Learning by doing research",
        body: "Active participation in real projects is the main vehicle for learning.",
      },
      {
        title: "Applied methodological rigor",
        body: "Every project follows standards aligned with the current scientific environment.",
      },
      {
        title: "Teamwork with structure",
        body: "Guided teams with defined deliverables and clear roles from day one.",
      },
      {
        title: "Clear expectations from the start",
        body: "We guide the process; editorial acceptance depends on each journal.",
      },
      {
        title: "Every member owns the outcome",
        body: "Project progress depends on the commitment of each team member.",
      },
      {
        title: "Growing step by step",
        body: "Levels that gradually increase methodological complexity and autonomy.",
      },
    ],
  },
  experience: {
    eyebrow: "The STM experience",
    title: "A structured path, from idea to manuscript.",
    lead: "A collaborative pathway designed to guide participants through the whole research process.",
    steps: [
      { n: "01", title: "Inquiry", body: "Initial contact and project exploration." },
      { n: "02", title: "Profile review", body: "Background, interest and availability assessment." },
      { n: "03", title: "Enrollment & group", body: "Placement according to availability and requirements." },
      { n: "04", title: "Initial session", body: "Introduction to mentors, team and project." },
      { n: "05", title: "Project development", body: "Collaborative research and continuous mentorship." },
      { n: "06", title: "Editorial process", body: "Manuscript preparation and submission guidance." },
      { n: "07", title: "Alumni & opportunities", body: "New projects and long-term collaboration." },
    ],
  },
  pathways: {
    eyebrow: "Research pathways",
    title: "Three pathways. Different starting points.",
    lead: "STM organizes projects by methodological complexity, workload and editorial risk. The STM Level classifies work, not people.",
    note: "Project availability may vary according to current cohort openings. Fees cover academic mentorship and scientific direction; they do not guarantee manuscript acceptance or publication.",
    priceLabel: "Per participant",
    seatsLabel: "Maximum seats",
    durationLabel: "Estimated duration",
    items: [
      {
        index: "01",
        level: "Basic level",
        title: "Basic pathway",
        work: "Case Report",
        description:
          "A first publication experience with close guidance and low methodological complexity.",
        bullets: [
          "Ideal for participants beginning their research journey",
          "Foundational scientific writing skills",
          "Case report, case with literature review or editorial",
        ],
        price: "$3,500 MXN",
        seats: "8 participants",
        duration: "1 to 2 months",
      },
      {
        index: "02",
        level: "Intermediate level",
        title: "Intermediate pathway",
        work: "Narrative Review",
        description:
          "Focused literature synthesis with structured academic mentorship and greater participant autonomy.",
        bullets: [
          "Deepen critical appraisal of the literature",
          "Strengthen scientific communication",
          "Narrative review, scoping review or case series",
        ],
        price: "$4,500 MXN",
        seats: "8 participants",
        duration: "1 to 3 months",
      },
      {
        index: "03",
        level: "Advanced level",
        title: "Advanced pathway",
        work: "Systematic Review",
        description:
          "Structured methodology and a rigorous process oriented to evidence synthesis and Q1–Q4 indexed journals.",
        bullets: [
          "Advanced research methodologies",
          "Meta-analysis by invitation or prior assessment",
          "Requires academic review by STM Direction",
        ],
        price: "from $6,000 MXN",
        seats: "6 participants",
        duration: "3 to 4 months",
      },
    ],
  },
  publications: {
    eyebrow: "Scientific research",
    titleTop: "Selected",
    titleAccent: "publications",
    view: "View",
    all: "See all on Google Scholar",
    allUrl: "https://scholar.google.com/citations?hl=en",
    items: [
      {
        meta: "Cureus · May 2026",
        title:
          "Testosterone Replacement Therapy as a Foundation for Body Composition Remodeling: Synergistic Roles of Resistance Training and Protein Intake",
        source: "Narrative Review · Cureus Journal of Medical Science",
      },
      {
        meta: "Springer Nature · May 2026",
        title:
          "Myeloperoxidase Antineutrophil Cytoplasmic Antibody (MPO-ANCA)-Associated Usual Interstitial Pneumonia: Pathogenic Mechanisms, Diagnostic Challenges, and Clinical Implications",
        source: "Springer Nature · Structured Narrative Review",
      },
      {
        meta: "Indexed journals · 2025–2026",
        title:
          "Systematic reviews, meta-analyses and clinical cases in surgery, internal medicine and public health",
        source: "20+ peer-reviewed publications in total",
      },
    ],
  },
  instagram: {
    eyebrow: "STM community",
    titleTop: "Follow us on",
    titleAccent: "Instagram",
    lead: "Publications, open calls and cohort milestones, told post by post.",
    handle: "@sharethemed",
    cta: "View profile on Instagram",
    profileUrl: "https://www.instagram.com/sharethemed/",
  },
  included: {
    eyebrow: "What's included",
    title: "A complete support structure.",
    lead: "A guided and collaborative experience designed to support your research development.",
    items: [
      { title: "Structured mentorship", body: "Guidance throughout the research process." },
      { title: "Scientific writing support", body: "Development of writing and communication skills." },
      { title: "Research methodology guidance", body: "Support in understanding and applying research methods." },
      { title: "Collaborative participation", body: "Work within a multidisciplinary team environment." },
      { title: "Administrative coordination", body: "Scheduling, communication and project organization." },
      { title: "Editorial orientation", body: "Guidance on manuscript preparation and dissemination." },
    ],
  },
  global: {
    eyebrow: "International environment",
    title: "Collaboration beyond borders.",
    lead: "STM brings together participants from different countries and professional backgrounds to create a collaborative research environment shaped by diverse perspectives.",
    items: [
      { title: "International participants", body: "Work alongside peers from various countries and build a global network." },
      { title: "Multidisciplinary collaboration", body: "Projects enriched by diverse expertise and backgrounds." },
      { title: "Mentorship environment", body: "Guided by experienced mentors committed to your growth." },
      { title: "Global exposure", body: "Insight into international research practices and standards." },
    ],
  },
  enroll: {
    eyebrow: "Participation & enrollment",
    title: "How to apply.",
    lead: "The admission process keeps groups functional and protects the academic quality of every project.",
    points: [
      "Create your account and complete the application with your academic profile and research interests.",
      "STM Academic Direction reviews your profile and confirms the pathway and available group.",
      "You secure your seat with the participation payment through the platform.",
      "Commitment to deliverables, deadlines and collaborative teamwork.",
    ],
    disclaimer:
      "STM offers flexible payment options, including alternatives for international participants. Acceptance and publication remain subject to each journal's editorial and peer-review process.",
    accountTitle: "Participant account and online payments",
    accountBody:
      "Create your account to submit your application, follow the academic review and, once accepted, pay your participation fee (MXN or USD) directly on the platform.",
  },
  contact: {
    eyebrow: "Contact",
    title: "Start your research journey.",
    lead: "Our goal is for you to develop a solid, practical understanding of medical research, one that genuinely supports your professional growth.",
    whatsapp: "WhatsApp",
    email: "Email",
    instagram: "Instagram",
  },
  footer: {
    rights: "All rights reserved.",
    disclaimer: "STM guides the research process; it does not guarantee manuscript publication.",
  },
  meta: {
    title: "Share The Med (STM) | Medical research mentorship",
    description:
      "Scientific mentorship program for students, residents and physicians: case reports, narrative reviews and systematic reviews with editorial guidance.",
  },
};

export const CONTACT = {
  whatsapp: "+52 56 5245 9625",
  whatsappHref: "https://wa.me/525652459625",
  email: "sharethemed@gmail.com",
  instagram: "https://www.instagram.com/sharethemed",
  instagramHandle: "@sharethemed",
  linkedin: "Share the Med",
  facebook: "Share the Med",
};

export const content: Record<Lang, Content> = { es, en };
